/**
 * frontend/src/pages/valuation/ValuationCompany.tsx
 *
 * VAL-COMPANY-PROFILE-1B — the "Company" step, between Engagement and Intake.
 * Facts about the business for the report's "The company" page. Record-only:
 * nothing here is scored and nothing here changes the number.
 *
 * Data provenance: nothing typed here lives only in the browser. Save writes
 * to the server; the grey "Unsaved changes" line shows whenever the form
 * differs from the last saved (or loaded) profile. A profile over 100% still
 * goes to the server — its 422 message is what the user sees.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { getValuationEngagement } from '@/api/valuation'
import { saveCompanyProfile } from '@/api/valuationCompany'
import { fetchIndustryFactFields } from '@/api/valuationIndustryFacts'
import { fetchFinancials } from '@/api/valuationFinancials'
import { formatApiError } from '@/utils/httpErrors'
import type { EngagementWithCompany, IndustryFactField } from '@/types/valuationCompany'
import { CompanyRows } from './_CompanyRows'
import {
  BLANK_COMPANY,
  companyFromEngagement,
  companyToPayload,
  isDirty,
  missingPctMessages,
  incompletePersonMessages,
  failingRowCount,
  saveBlockedMessage,
  joinName,
  BLANK_PERSON,
  pctTotal,
  type CompanyState,
} from './_companyProfile'
import { IndustryFacts } from './_IndustryFactsSection'
import { NO_OFFER, applyOffer, nameKey, offerFromReadings, type AgreementOffer, type Offered } from './_operatingAgreement'
import { factsDirty, factsFromSaved, factsToPayload, type FactsState } from './_industryFacts.ts'
import { ValuationStepper, nextStepPath } from './_shared'

const TEXT_AREAS: Array<{ key: keyof CompanyState & string; label: string }> = [
  { key: 'business_description', label: 'Business description' },
  { key: 'history', label: 'History' },
  { key: 'restrictions_agreements', label: 'Restrictions and agreements' },
  { key: 'facilities', label: 'Facilities' },
  { key: 'prior_sales', label: 'Prior sales of ownership interests' },
  { key: 'geography', label: 'Geography' },
  { key: 'assets_liabilities_included', label: 'Assets and liabilities included' },
]

function Field({ id, label, children }: { id: string; label: string; children: ReactNode }) {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="text-sm font-medium text-gray-900">{label}</label>
      {children}
    </div>
  )
}

export default function ValuationCompany() {
  const { valuationId } = useParams<{ valuationId: string }>()
  const navigate = useNavigate()

  const [engagement, setEngagement] = useState<EngagementWithCompany | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [form, setForm] = useState<CompanyState>(BLANK_COMPANY)
  const [saved, setSaved] = useState<CompanyState>(BLANK_COMPANY)
  const [saving, setSaving] = useState(false)
  const [tried, setTried] = useState(false)
  const [factFields, setFactFields] = useState<IndustryFactField[]>([])
  const [setLabel, setSetLabel] = useState<string | null>(null)
  const [setLabels, setSetLabels] = useState<Record<string, string>>({})
  const [facts, setFacts] = useState<FactsState>({})
  const [savedFacts, setSavedFacts] = useState<FactsState>({})
  const [factsError, setFactsError] = useState<string | null>(null)
  const [offer, setOffer] = useState<AgreementOffer>(NO_OFFER)
  // Where each name that Use these filled came from; on screen only.
  const [ownerSources, setOwnerSources] = useState<Record<string, Offered[]>>({})
  const [personSources, setPersonSources] = useState<Record<string, Offered[]>>({})

  // VAL-OA-SCREEN-1: what the operating agreement reading found. A failed
  // fetch leaves the step exactly as it was.
  useEffect(() => {
    if (!valuationId) return
    let live = true
    fetchFinancials(valuationId)
      .then(view => { if (live) setOffer(offerFromReadings(view.operating_agreements)) })
      .catch(() => undefined)
    return () => { live = false }
  }, [valuationId])

  useEffect(() => {
    if (!valuationId) return
    let live = true
    getValuationEngagement(valuationId)
      .then(en => {
        if (!live) return
        const state = companyFromEngagement(en as EngagementWithCompany)
        setEngagement(en as EngagementWithCompany)
        setForm(state)
        setSaved(state)
        // K14: no industry and nothing kept -> there is nothing to ask the server.
        const industry = (en.industry ?? '').trim()
        if (!industry && !(en as EngagementWithCompany).industry_facts_kept) return
        fetchIndustryFactFields(industry)
          .then(res => {
            if (!live) return
            const loadedFacts = factsFromSaved(res.fields, (en as EngagementWithCompany).industry_facts)
            setFactFields(res.fields)
            setSetLabel(res.set_label)
            setSetLabels(res.set_labels)
            setFacts(loadedFacts)
            setSavedFacts(loadedFacts)
          })
          .catch((err: unknown) => { if (live) setFactsError(formatApiError(err)) })
      })
      .catch((err: unknown) => { if (live) setLoadError(formatApiError(err)) })
    return () => { live = false }
  }, [valuationId])

  const set = <K extends keyof CompanyState>(key: K, value: CompanyState[K]) =>
    setForm(f => ({ ...f, [key]: value }))

  function useAgreement() {
    const had = (rows: Array<{ name: string }>) => new Set(rows.map(r => nameKey(r.name)))
    const hadOwners = had(form.owners)
    const hadPeople = had(form.key_people.map(k => ({ name: joinName(k.first, k.last) })))
    const group = (items: Offered[], skip: Set<string>) => {
      const out: Record<string, Offered[]> = {}
      for (const o of items) {
        const k = nameKey(o.name)
        if (!skip.has(k)) (out[k] ??= []).push(o)
      }
      return out
    }
    setOwnerSources(prev => ({ ...prev, ...group(offer.owners, hadOwners) }))
    setPersonSources(prev => ({ ...prev, ...group(offer.people, hadPeople) }))
    setForm(f => applyOffer(f, offer))
  }

  const notesFor = (sources: Record<string, Offered[]>) => (row: { name: string }): string[] => {
    const found = sources[nameKey(row.name)] ?? []
    return found.map(o => o.source)
      .filter((t, i, all) => all.indexOf(t) === i)
  }

  // A named row with no % is never sent as 0 — Save is blocked and the row named.
  const mixErrors = missingPctMessages('revenue', form.revenue_mix.map(r => ({ name: r.line, pct: r.pct })))
  const ownerErrors = missingPctMessages('ownership', form.owners)
  const personErrors = incompletePersonMessages(form.key_people)
  const failing = failingRowCount(mixErrors, ownerErrors, personErrors)

  /** Save what is on screen; true only when the server accepted it. */
  async function save(): Promise<boolean> {
    if (!valuationId) return false
    setTried(true)
    if (failing > 0) return false
    const snapshot = form
    setSaving(true)
    try {
      const factsSnapshot = facts
      if (factsDirty(factsSnapshot, savedFacts)) {
        await saveCompanyProfile(valuationId, companyToPayload(snapshot), factsToPayload(factFields, factsSnapshot))
        setSavedFacts(factsSnapshot)
      } else {
        await saveCompanyProfile(valuationId, companyToPayload(snapshot))
      }
      setSaved(snapshot)
      return true
    } catch (err: unknown) {
      toast.error(formatApiError(err))
      return false
    } finally {
      setSaving(false)
    }
  }

  async function saveAndContinue() {
    if (!(await save())) return
    const next = nextStepPath('company', valuationId)
    if (next) navigate(next)
  }

  if (loadError) {
    return (
      <div className="mx-auto max-w-7xl p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">{loadError}</div>
      </div>
    )
  }
  if (!engagement) {
    return (
      <div className="mx-auto max-w-7xl space-y-6 p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <ValuationStepper current="company" engagementId={valuationId} />
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Company profile</h1>
        <p className="mt-1 text-sm text-gray-500">
          Facts about the business for the report&apos;s &apos;The company&apos; page. Not scored — nothing here changes the number.
        </p>
      </div>

      <form className="space-y-5" onSubmit={e => { e.preventDefault(); void save() }}>
        {TEXT_AREAS.slice(0, 2).map(f => (
          <Field key={f.key} id={`co-${f.key}`} label={f.label}>
            <Textarea id={`co-${f.key}`} value={form[f.key] as string} onChange={e => set(f.key, e.target.value as never)} />
          </Field>
        ))}
        <CompanyRows
          title="Revenue mix"
          singular="Revenue"
          rows={form.revenue_mix}
          columns={[
            { key: 'line', label: 'line', placeholder: 'Product or service line' },
            { key: 'pct', label: '%', placeholder: '%', numeric: true },
          ]}
          blank={{ line: '', pct: '' }}
          total={pctTotal(form.revenue_mix)}
          rowErrors={tried ? mixErrors : undefined}
          onChange={rows => set('revenue_mix', rows)}
        />
        {offer.readFiles.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
            <span>{`Read from your operating agreement (${offer.readFiles.join(', ')}). Check the names and roles, then save.`}</span>
            <Button type="button" variant="outline" size="sm" onClick={useAgreement}>Use these</Button>
          </div>
        )}
        {offer.notRead && (
          <p className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
            We found your operating agreement but could not read it. Enter owners and key people below.
          </p>
        )}
        <CompanyRows
          title="Owners"
          singular="Owner"
          rows={form.owners}
          columns={[
            { key: 'name', label: 'name', placeholder: 'Owner name' },
            { key: 'pct', label: '%', placeholder: '%', numeric: true },
          ]}
          blank={{ name: '', pct: '' }}
          total={pctTotal(form.owners)}
          rowErrors={tried ? ownerErrors : undefined}
          note={notesFor(ownerSources)}
          onChange={rows => set('owners', rows)}
        />
        <Field id="co-legal_entity_type" label="Legal entity type">
          <Input id="co-legal_entity_type" value={form.legal_entity_type} onChange={e => set('legal_entity_type', e.target.value)} />
        </Field>
        {TEXT_AREAS.slice(2, 4).map(f => (
          <Field key={f.key} id={`co-${f.key}`} label={f.label}>
            <Textarea id={`co-${f.key}`} value={form[f.key] as string} onChange={e => set(f.key, e.target.value as never)} />
          </Field>
        ))}
        <CompanyRows
          title="Key people"
          singular="Person"
          rows={form.key_people}
          columns={[
            { key: 'first', label: 'first name', placeholder: 'First name' },
            { key: 'last', label: 'last name', placeholder: 'Last name' },
            { key: 'role', label: 'role', placeholder: 'Role' },
            { key: 'contact', label: 'phone or email', placeholder: 'Phone or email' },
          ]}
          blank={BLANK_PERSON}
          addLabel="Add row"
          rowErrors={tried ? personErrors : undefined}
          note={row => notesFor(personSources)({ name: joinName(row.first, row.last) })}
          onChange={rows => set('key_people', rows)}
        />
        {TEXT_AREAS.slice(4).map(f => (
          <Field key={f.key} id={`co-${f.key}`} label={f.label}>
            <Textarea id={`co-${f.key}`} value={form[f.key] as string} onChange={e => set(f.key, e.target.value as never)} />
          </Field>
        ))}
        <Field id="co-transaction_structure" label="Transaction structure">
          <select
            id="co-transaction_structure"
            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            value={form.transaction_structure}
            onChange={e => set('transaction_structure', e.target.value as CompanyState['transaction_structure'])}
          >
            <option value="">Not stated</option>
            <option value="asset">Asset</option>
            <option value="stock">Stock</option>
            <option value="n/a">Not applicable</option>
          </select>
        </Field>

        <IndustryFacts
          fields={factFields}
          setLabel={setLabel}
          current={setLabel ?? (engagement.industry_label ?? engagement.industry ?? '').trim()}
          kept={engagement.industry_facts_kept ?? null}
          setLabels={setLabels}
          state={facts}
          onChange={(key, value) => setFacts(f => ({ ...f, [key]: value }))}
          loadError={factsError}
        />

        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" disabled={saving}>Save</Button>
          <Button type="button" variant="outline" disabled={saving} onClick={() => void saveAndContinue()}>
            Save and continue to Financials
          </Button>
          {(isDirty(form, saved) || factsDirty(facts, savedFacts)) && <span className="text-sm text-gray-500">Unsaved changes</span>}
        </div>
        {tried && failing > 0 && <p role="alert" className="text-sm font-medium text-red-600">{saveBlockedMessage(failing)}</p>}
      </form>
    </div>
  )
}
